/**
 * f13.1: the "Export CSV" page action, identical on the cycle workflow page,
 * the review screen and the forecast grid toolbar (prototype `review.html`,
 * `forecast-grid.html`). Never a navigation entry.
 *
 * Disabled before release as a courtesy; the server refuses a forced request
 * with `409 CYCLE_NOT_EXPORTABLE` regardless -- the button is not the boundary.
 *
 * f16.2 (Q-f16.2-4): rendered only when the session carries `cycle:export`.
 * A read-only role lacks it; its forced request is refused `read_only_role`.
 */

import type { CSSProperties } from 'react';
import { CheckCircle2, Download, Loader2, TriangleAlert, X } from 'lucide-react';

import '@/styles/cycle-export.css';
import { EXPORT_CYCLE } from '@/session/permissions';
import { useSessionScope } from '@/session/SessionScopeContext';

import { useCycleExport } from './useCycleExport';

const NOT_RELEASED = new Set(['DRAFT', 'PREPARED']);
const NOT_RELEASED_HINT = 'Available once the cycle is released for input';

interface ExportCycleCsvButtonProps {
  cycleId: string;
  stateCode: string;
  size?: 'sm';
}

export function ExportCycleCsvButton({ cycleId, stateCode, size }: ExportCycleCsvButtonProps) {
  const { start, preparing, progress, outcome, dismiss } = useCycleExport(cycleId);
  const { can } = useSessionScope();
  if (!can(EXPORT_CYCLE)) return null;
  const unreleased = NOT_RELEASED.has(stateCode);
  const sizeClass = size === 'sm' ? ' btn-sm' : '';

  return (
    <>
      <button
        type="button"
        className={`btn btn-secondary cx-button${sizeClass}`}
        onClick={start}
        disabled={unreleased || preparing}
        title={unreleased ? NOT_RELEASED_HINT : undefined}
        aria-busy={preparing}
        style={{ '--cx-progress': `${progress}%` } as CSSProperties}
        data-preparing={preparing || undefined}
      >
        {preparing ? (
          <>
            <Loader2 className="spin" aria-hidden="true" /> Preparing… {progress} %
          </>
        ) : (
          <>
            <Download aria-hidden="true" /> Export CSV
          </>
        )}
      </button>

      {outcome && (
        <div
          className={`alert ${outcome.tone === 'success' ? 'alert-success' : 'alert-danger'} cx-toast`}
          role={outcome.tone === 'success' ? 'status' : 'alert'}
        >
          {outcome.tone === 'success' ? (
            <CheckCircle2 aria-hidden="true" />
          ) : (
            <TriangleAlert aria-hidden="true" />
          )}
          <div className="a-title">{outcome.message}</div>
          <button type="button" className="cx-toast-close" onClick={dismiss} aria-label="Dismiss">
            <X aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}
